<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(:title="isEdit ? $t('panel.edit_panel_claim') : $t('panel.create_panel_claim')" :cancel-action-label="$t('cancel')" :save-action-label="isEdit ? $t('save') : $t('panel.create_claim')" :loading="isLoading" :disable-save="isError || isFetching" @back="onCloseDialog" @cancel="onCloseDialog" @save="onSaveClaim")
      yz-dialog-fullscreen-content.pa-6
        // Loading state (full width)
        template(v-if="isFetching")
          create-panel-claim-loader

        // Error state (full width)
        template(v-else-if="isError")
          yz-empty-state-card(icon="$yzi-alert-triangle" :title="$t('panel.unable_to_load_invoice_details')" :description="$t('panel.check_invoice_id_and_retry')" border-color="yz-danger-500" icon-color="yz-danger-500" :action-label="$t('retry')" @action-click="fetchInvoiceDetails")

        // Main content (show when not loading and no error)
        template(v-else)
          v-form(ref="form")
            v-row(no-gutters style="min-height: calc(100dvh - 60px - 48px);")
              // Left Column - Invoice Details
              v-col.text-neutral-700(cols="7" xl="8")
                .yz-heading-9 {{ $t('panel.invoice_details') }}
                .yz-regular-body-xs {{ $t('panel.invoice_details_description') }}

                yz-card.mt-6(outlined)
                  // Header section with billing, patient and invoice details
                  .d-flex.pa-4
                    // Billing details
                    .flex-grow-1
                      .yz-medium-body-xs.text-neutral-500.mb-2 {{ $t('panel.billing_details') }}
                      .d-flex.align-center.mb-1
                        .yz-medium-body-sm {{ billingName || 'N/A' }}
                        yz-btn.ml-2(v-if="billingName" icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(billingName)")
                      .d-flex.align-center
                        .yz-regular-body-sm {{ $t('panel.staff_id_field') }}: {{ staffId || 'N/A' }}
                        yz-btn.ml-2(v-if="staffId" icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(staffId)")

                    yz-divider.mx-5(vertical height="80px")

                    // Patient details
                    .flex-grow-1
                      .yz-medium-body-xs.text-neutral-500.mb-2 {{ $t('panel.patient_details') }}
                      .d-flex.align-center.mb-1
                        .yz-medium-body-sm {{ patientName || 'N/A' }}
                        yz-btn.ml-2(v-if="patientName" icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(patientName)")
                      .d-flex.align-center
                        .yz-regular-body-sm {{ $t('panel.chit_no') }}: {{ chitNumber || 'N/A' }}
                        yz-btn.ml-2(v-if="chitNumber" icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(chitNumber)")

                    yz-divider.mx-5(vertical height="80px")

                    // Invoice details
                    .flex-grow-1
                      .yz-medium-body-xs.text-neutral-500.mb-2 {{ $t('panel.invoice_details') }}
                      .d-flex.align-center.mb-1
                        .yz-regular-body-sm {{ $t('panel.invoice_no') }}: {{ invoiceNumber || 'N/A' }}
                        yz-btn.ml-2(v-if="invoiceNumber" icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(invoiceNumber)")
                      .d-flex.align-center
                        .yz-regular-body-sm {{ $t('panel.visit') }}: {{ visitDateTime || 'N/A' }}
                        yz-btn.ml-2(v-if="visitDateTime" icon="$yzi-copy-03" icon-only size="sm" @click="onCopyText(visitDateTime)")


                // Treatment items table card
                yz-card.mt-6.px-4.py-3(outlined)
                  .yz-medium-body-xs.text-neutral-500.mb-4 {{ $t('panel.treatment_items') }}

                  template(v-if="treatmentItems.length > 0")
                    yz-data-table(:headers="tableHeaders" :items="treatmentItems" :items-per-page="pageSize" :page="currentPage" @update:options="onOptionsUpdate")
                      template(v-slot:item.name="{ item }")
                        .yz-medium-body-sm {{ item.name }}

                      template(v-slot:item.rate="{ item }")
                        .yz-medium-body-sm {{ item.rate | displayPrice($currency) }}

                      template(v-slot:item.qty="{ item }")
                        .yz-medium-body-sm {{ item.qty }}

                      template(v-slot:item.total="{ item }")
                        .yz-medium-body-sm {{ item.total | displayPrice($currency) }}

                      template(v-slot:footer)
                        yz-divider.my-0
                        // Pagination and total summary
                        .d-flex.justify-space-between.align-center.py-2.px-3
                          .yz-regular-body-xs.no-wrap
                            | {{ $t('panel.total') }}:
                            span.yz-medium-body-xs.ml-1 {{ totalAmount | displayPrice($currency) }}
                            |  ({{ totalItems }} {{ $t('panel.items') }})
                          yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalItems" type="item" arrow)

                  template(v-else)
                    .d-flex.align-center.justify-center.py-8
                      .yz-regular-body-sm.text-neutral-500 {{ $t('panel.no_treatment_items_found') }}

              // Divider Column
              yz-divider.mx-6(vertical)

              // Right Column - Panel Claim Details
              v-col.text-neutral-700
                .yz-heading-9 {{ $t('panel.panel_claim_details') }}
                .yz-regular-body-xs {{ $t('panel.panel_claim_details_description') }}

                // Panel summary card
                yz-card.mt-6.pa-3(color="yz-neutral-300" outlined style="border-radius: 10px;")
                  // Header row
                  v-row.mb-2(no-gutters, align="start")
                    v-col(cols="7")
                      v-row(no-gutters, align="center")
                        v-col(cols="auto")
                          .yz-medium-body-sm.text-neutral-700 {{ effectivePanelName }}
                        yz-divider.mx-1(v-if="isEdit" vertical, color="yz-neutral-400")
                        v-col(v-if="isEdit")
                          .yz-medium-body-sm.text-neutral-500 {{ $t('panel.claim') }} {{ '#' + parentClaim.rid }}
                      .d-flex.align-center.gap-2.mt-1
                        .yz-heading-6.text-neutral-700
                          span(v-if="isEdit") {{ displayAmount | displayPrice($currency) }}
                          span(v-else) {{ invoiceAmount | displayPrice($currency) }}
                        yz-btn(v-if="isEdit && currentClaimStatus === 'submitted'" icon="$yzi-edit-05" icon-only size="md" color="yz-neutral-500" @click="onEditAmount")
                        claim-status-badge(v-if="isEdit && parentClaim.status === 'received'" :status="parentClaim.status" size="sm" dot-size="6px")

                    v-col(v-if="isEdit && parentClaim.status !== 'received'")
                      // Status dropdown row
                      .yz-medium-body-sm.text-neutral-700 {{ $t('panel.claim_status') }}
                      yz-dropdown.mt-1(v-model="currentClaimStatus" :items="statusOptions" size="lg")

                template(v-if="parentClaim && parentClaim.status === 'received'")
                  // Payment summary section for received status
                  received-payment-summary-card.mt-6(:admin-fee="editedData.adminFee" :rejected-amount="editedData.rejectedAmount" :received-amount="receivedAmountDisplay" :received-date="editedData.date")

                template(v-else)
                  // Record deductions section
                  .mt-6(v-if="isEdit && (currentClaimStatus === 'approved' || currentClaimStatus === 'received')")
                    .yz-medium-body-xs.text-neutral-500.mb-2 {{ $t('panel.record_deductions') }}
                    admin-fee(:admin-fee-value.sync="editedData.adminFee" :admin-fee-type.sync="editedData.adminFeeType" :admin-fee-percentage.sync="editedData.adminFeePercentage" :base-amount="adminFeeBaseAmount" :readonly="currentClaimStatus === 'received' && parentClaim.status === 'approved'")
                    rejected-amount(v-model="editedData.rejectedAmount" :balance-assigned="balanceAssigned" :readonly="currentClaimStatus === 'received' && parentClaim.status === 'approved'" @assign-balance="onAssignBalance" @updated="onRejectedAmountUpdated")

                    // Status amount summary
                    .d-flex.justify-space-between.align-center.mt-5
                      .yz-medium-body-sm.text-neutral-700.font-semibold {{ amountLabelByStatus }}
                      .yz-medium-body-sm.text-success-500.font-semibold {{ finalAmountAfterDeductions | displayPrice($currency) }}

                  // Form fields
                  .mt-6
                    .yz-medium-body-sm.text-neutral-700.mb-1 {{ dateLabelByStatus }}
                    yz-text-field(v-model="formattedDate" :placeholder="datePickerPlaceholder" prepend-inner-icon="$yzi-calendar" readonly clickable clearable size="lg" required :rules="dateRules" :required-error-message="$t('panel.date_required')" @click="showDatePickerDialog = true" @click:clear="editedData.date = null")

                .mt-6
                  .yz-medium-body-sm.text-neutral-700.mb-1 {{ $t('panel.remarks') }}
                  yz-textarea(v-model="editedData.remarks" :placeholder="$t('panel.write_remarks_here')" rows="4")

                yz-divider.my-6

                // Upload photo section
                .yz-medium-body-xs.text-neutral-700.mb-4 {{ $t('panel.upload_photo') }}
                yz-upload-card(v-model="uploadedPhotos" multiple, append-files, accept="image/*" :primary-title="$t('panel.click_to_upload')" :secondary-title="$t('panel.or_drag_and_drop')" :description="$t('panel.jpg_png_max_5mb')" upload-mode="immediate" @upload-batch-complete="onUploadBatchCompleted")

                // QR code section
                .d-flex.mt-4.gap-4.align-center
                  yz-upload-qr-databin(:qr-size="100" @databin-created="onQrCreated" @error="onQrError")
                  div
                    .yz-medium-body-xs.text-neutral-700.mb-1 {{ $t('panel.add_from_other_devices') }}
                    .yz-regular-body-xs.text-neutral-700 {{ $t('panel.scan_qr_instruction') }}
                // Databin synchronization component (hidden)
                yz-databin-sync(v-if="databinKey" v-model="allSyncedFiles" :added-items.sync="uploadedPhotos" :databin-key="databinKey" @error="onDatabinError" ref="databinSync")

                // Preview files
                .mt-4
                  yz-file-preview(v-model="originalFiles" :to-delete-items.sync="originalFileToDelete" :truncate-length="10")
                  yz-file-preview(v-model="allSyncedFiles" :to-delete-items.sync="uploadedFileToDelete" :truncate-length="10" @deleted="onDeletedUploadedFiles")
          // Assign Balance Dialog
          assign-balance-confirmation-dialog(v-if="showAssignBalanceDialog" v-model="showAssignBalanceDialog" type="single" :patient-name="patientName" :invoice-number="invoiceNumber" :outstanding-amount="editedData.rejectedAmount" @confirmed="onAssignBalanceConfirmed" @canceled="onAssignBalanceCanceled")

          // Edit Amount Dialog
          edit-single-claim-amount-dialog(v-if="showEditAmountDialog" v-model="showEditAmountDialog" :current-amount="displayAmount" @confirmed="onEditAmountConfirmed")

          // Change Claim Status Confirmation Dialog
          change-claim-status-confirmation-dialog(v-if="showChangeStatusDialog" v-model="showChangeStatusDialog" :current-status="parentClaim.status" :new-status="currentClaimStatus" :admin-fee="editedData.adminFee" :rejected-amount="editedData.rejectedAmount" @confirmed="handleUpdateClaim")

          // Date Picker Dialog
          yz-dialog(v-model="showDatePickerDialog" max-width="380px")
            yz-card
              yz-dialog-normal-content.px-2.pt-2.pb-0
                yz-date-picker(v-model="editedData.date" scrollable, @change="showDatePickerDialog = false")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import EditSingleClaimAmountDialog from '@/components/clinic/panel/dialogs/single/EditSingleClaimAmountDialog.vue'
import AssignBalanceConfirmationDialog from '@/components/clinic/panel/dialogs/AssignBalanceConfirmationDialog.vue'
import ChangeClaimStatusConfirmationDialog from '@/components/clinic/panel/dialogs/ChangeClaimStatusConfirmationDialog.vue'
import AdminFee from '@/components/clinic/panel/claims/AdminFee.vue'
import RejectedAmount from '@/components/clinic/panel/claims/RejectedAmount.vue'
import CreatePanelClaimLoader from '@/components/clinic/panel/claims/loader/CreatePanelClaimLoader.vue'
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'
import ReceivedPaymentSummaryCard from '@/components/clinic/panel/claims/ReceivedPaymentSummaryCard.vue'

export default {
  components: { EditSingleClaimAmountDialog, AssignBalanceConfirmationDialog, ChangeClaimStatusConfirmationDialog, AdminFee, RejectedAmount, CreatePanelClaimLoader, ClaimStatusBadge, ReceivedPaymentSummaryCard },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    panelName: {
      type: String,
      default: ''
    },
    panelId: {
      type: [String, Number],
      default: ''
    },
    invoiceAmount: {
      type: Number,
      default: 0
    },
    invoiceLeadPid: {
      type: [String, Number],
      default: ''
    },
    invoiceUid: {
      type: String,
      default: ''
    },
    claimStatus: {
      type: String,
      default: 'new'
    },
    visitDate: {
      type: String,
      default: ''
    },
    parentClaim: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      // Page state
      isLoading: false,
      isError: false,
      isFetching: true,
      isInitialized: false,
      // Dialogs
      showEditAmountDialog: false,
      showAssignBalanceDialog: false,
      showChangeStatusDialog: false,
      showDatePickerDialog: false,
      // Form data
      invoice: null,
      currentClaimStatus: 'submitted',
      editedData: {
        date: null,
        remarks: '',
        editClaimAmount: { value: null, remarks: null },
        adminFee: null,
        adminFeeType: 'fixed',
        adminFeePercentage: null,
        rejectedAmount: null,
        receivedAmount: null,
        changeStatusRemarks: null,
        assignBalance: []
      },
      // Pagination data
      currentPage: 1,
      pageSize: 10,
      // Track sort state to detect changes
      sortBy: [],
      sortDesc: [],
      // Table headers
      tableHeaders: [
        { text: this.$t('panel.item_name'), value: 'name', width: '50%' },
        { text: this.$t('panel.rate'), value: 'rate', align: 'right' },
        { text: this.$t('panel.qty'), value: 'qty', align: 'center' },
        { text: this.$t('panel.total'), value: 'total', align: 'right' }
      ],
      // Databin key for QRcode upload
      databinKey: null,
      originalFiles: [],
      allSyncedFiles: [],
      uploadedPhotos: [],
      uploadedFileToDelete: [],
      originalFileToDelete: []
    }
  },
  mounted() {
    // Validate required data
    if (!this.isEdit && !this.invoiceLeadPid) {
      this.isError = true
      return
    }

    // Fetch invoice details for both edit and create modes
    this.fetchInvoiceDetails()

    // Set date for both edit and create modes
    this.editedData.date = this.getDateByStatus()

    // Set mode-specific data
    if (this.isEdit) {
      this.editedData.remarks = this.parentClaim.remarks
      this.originalFiles = this.parentClaim.documents || []
      this.currentClaimStatus = this.parentClaim.status

      this.editedData.adminFee = this.parentClaim.admin_fee || null
      this.editedData.adminFeeType = this.parentClaim.admin_fee_percentage ? 'percentage' : 'fixed'
      this.editedData.adminFeePercentage = this.parentClaim.admin_fee_percentage || null
      this.editedData.rejectedAmount = this.parentClaim.rejected_amount || null
    }

    // Mark as initialized after all setup is complete
    // Use nextTick to ensure all watchers have processed initial values
    this.$nextTick(() => {
      this.isInitialized = true
    })
  },
  watch: {
    currentClaimStatus: {
      handler() {
        this.handleClaimStatusChange()
      },
      immediate: true
    },
    needToAssignBalance(newValue, oldValue) {
      // Only auto-open dialog when transitioning from false to true after initialization
      if (this.isInitialized && !oldValue && newValue) {
        this.showAssignBalanceDialog = true
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    isEdit() {
      return !!this.parentClaim
    },
    statusOptions() {
      const baseOptions = [
        { text: this.$t('panel.status_submitted'), value: 'submitted' },
        { text: this.$t('panel.status_approved'), value: 'approved' },
        { text: this.$t('panel.status_rejected'), value: 'rejected' }
      ]

      // Add 'Received' option only if parent claim status is not 'rejected'
      if (this.isEdit && this.parentClaim && this.parentClaim.status !== 'rejected') {
        baseOptions.push({ text: this.$t('panel.status_received'), value: 'received' })
      }

      return baseOptions
    },
    billingName() {
      if (!this.invoice) return null
      return this.invoice.staff_name || null
    },
    patientName() {
      if (!this.invoice) return null
      return this.invoice.name || null
    },
    invoiceNumber() {
      if (!this.invoice) return null
      return this.invoice.invoice_no ? this.invoice.invoice_no.toString() : null
    },
    staffId() {
      if (!this.invoice) return null
      return this.invoice.staff_id || null
    },
    chitNumber() {
      if (!this.invoice) return null
      return this.invoice.chit_number || null
    },
    treatmentItems() {
      if (!this.invoice || !this.invoice.treatments || !Array.isArray(this.invoice.treatments)) return []

      const items = []

      this.invoice.treatments.forEach(treatment => {
        // Validate required treatment fields
        if (!treatment || !treatment.name) return

        // Treatment item
        const price = treatment.price || 0
        const quantity = treatment.quantity || 1

        items.push({
          name: treatment.name,
          rate: price,
          qty: quantity,
          total: price * quantity
        })
      })

      return items
    },
    totalItems() {
      return this.treatmentItems.length
    },
    totalAmount() {
      // Use amount from API response directly
      if (!this.invoice) return 0
      return this.invoice.amount || 0
    },
    visitDateTime() {
      if (!this.visitDate) return ''

      try {
        const dateTime = DateTime.fromISO(this.visitDate)
        return dateTime.toFormat('dd/MM/yyyy, h:mm a')
      } catch (error) {
        console.error('Error formatting date:', error)
        return null
      }
    },
    effectivePanelName() {
      return this.isEdit ? this.parentClaim.panel?.name : this.panelName
    },
    effectivePanelId() {
      return this.isEdit ? this.parentClaim.panel?.id : this.panelId
    },
    displayAmount() {
      if (!this.isEdit || !this.parentClaim) return 0

      const status = (this.currentClaimStatus || '').toLowerCase()

      // Prioritize editedData.editClaimAmount.value if it exists and status is "submitted"
      if (status === 'submitted' && this.editedData.editClaimAmount.value !== null) {
        return this.editedData.editClaimAmount.value
      }

      // Change from approved to rejected or rejected to approved should show submitted_amount
      if ((this.parentClaim.status === 'approved' && status === 'rejected') || (this.parentClaim.status === 'rejected' && status === 'approved')) {
        return this.parentClaim.submitted_amount
      }

      // Change from approved to received should show approved_amount
      if (this.parentClaim.status === 'approved' && status === 'received') {
        return this.parentClaim.approved_amount
      }

      const statusAmountMap = {
        submitted: this.parentClaim.submitted_amount,
        approved: this.parentClaim.approved_amount,
        rejected: this.parentClaim.rejected_amount,
        received: this.parentClaim.received_amount
      }

      return statusAmountMap[status] || this.parentClaim.submitted_amount
    },
    dateLabelByStatus() {
      return this.getLabelByStatus('date', this.$t('panel.submitted_date'))
    },
    amountLabelByStatus() {
      return this.getLabelByStatus('amount', this.$t('panel.submitted_amount'))
    },
    finalAmountAfterDeductions() {
      if (!this.isEdit || !this.parentClaim) return 0

      const baseAmount = this.parentClaim.submitted_amount || null

      if (baseAmount === null) return 0

      const rejectedAmount = this.editedData.rejectedAmount || 0

      // For 'approved' status: only deduct rejected amount (no admin fee)
      if (this.currentClaimStatus === 'approved') {
        return baseAmount - rejectedAmount
      }

      // For 'received' or 'rejected' status: deduct both admin fee and rejected amount
      const adminFeeAmount = this.editedData.adminFee || 0
      return baseAmount - adminFeeAmount - rejectedAmount
    },
    needToAssignBalance() {
      if (!this.isEdit || !this.parentClaim) return false

      const rejectedAmountChanged = this.parentClaim.rejected_amount !== this.editedData.rejectedAmount && this.editedData.rejectedAmount !== null
      const isApproveOrReceived = this.currentClaimStatus === 'approved' || this.currentClaimStatus === 'received'

      return rejectedAmountChanged && isApproveOrReceived
    },
    balanceAssigned() {
      // Balance is assigned if we have items in assignBalance array
      return this.editedData.assignBalance.length > 0
    },
    receivedAmountDisplay() {
      // Use editedData.receivedAmount if available, otherwise use parentClaim.received_amount
      return this.editedData.receivedAmount || this.parentClaim.received_amount || 0
    },
    adminFeeBaseAmount() {
      if (!this.isEdit || !this.parentClaim) return 0
      const submittedAmount = this.parentClaim.submitted_amount || 0
      const rejectedAmount = this.editedData.rejectedAmount || 0
      return submittedAmount - rejectedAmount
    },
    formattedDate() {
      if (!this.editedData.date) return ''
      return DateTime.fromISO(this.editedData.date).toFormat('dd/MM/yyyy')
    },
    dateRules() {
      return [
        v => !!v || this.$t('panel.date_required')
      ]
    },
    datePickerPlaceholder() {
      if (!this.isEdit || !this.parentClaim) {
        return this.$t('panel.select_submitted_date')
      }

      const statusPlaceholderMap = {
        submitted: this.$t('panel.select_submitted_date'),
        approved: this.$t('panel.select_approved_date'),
        rejected: this.$t('panel.select_rejected_date'),
        received: this.$t('panel.select_received_date')
      }

      return statusPlaceholderMap[this.currentClaimStatus] || this.$t('panel.select_submitted_date')
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    getDocumentIds() {
      const originalFileIds = this.originalFiles.map(file => file.hid).filter(Boolean)
      const newFileIds = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
      return [...originalFileIds, ...newFileIds]
    },
    addDocumentsToPayload(payload) {
      const documentIds = this.getDocumentIds()
      if (documentIds.length > 0) {
        payload.documents = documentIds
      }
    },
    async cleanupDeletedFiles() {
      const filesToDelete = [...this.originalFileToDelete, ...this.uploadedFileToDelete]

      if (filesToDelete.length > 0) {
        const deleteIds = filesToDelete.map(file => file.hid).filter(Boolean)
        if (deleteIds.length > 0) {
          try {
            await this.$axios.post('/documents/bulk_delete', { documents: deleteIds })
          } catch (error) {
            console.error('Error deleting files:', error)
            await this.showMessage(['error', 'Failed to delete some files'])
          }
        }
      }
    },
    getLabelByStatus(suffix, defaultLabel) {
      if (!this.isEdit || !this.parentClaim) {
        return defaultLabel
      }

      const statusLabelMap = {
        submitted: `${this.$t('panel.submitted_date_label')} ${suffix}`.replace(' date', ''),
        approved: `${this.$t('panel.approved_date_label')} ${suffix}`.replace(' date', ''),
        rejected: `${this.$t('panel.rejected_date_label')} ${suffix}`.replace(' date', ''),
        received: `${this.$t('panel.received_date_label')} ${suffix}`.replace(' date', '')
      }

      return statusLabelMap[this.currentClaimStatus] || defaultLabel
    },
    handleClaimStatusChange() {
      this.editedData.date = this.getDateByStatus()
      this.editedData.editClaimAmount = { value: null, remarks: null }

      // Simple check: are we reverting to original status?
      if (this.currentClaimStatus === this.parentClaim?.status) {
        this.revertToOriginalStatus()
        return
      }

      // Otherwise, handle status transitions
      this.handleStatusTransition()
    },
    revertToOriginalStatus() {
      this.editedData.adminFee = this.parentClaim.admin_fee
      this.editedData.adminFeeType = this.parentClaim.admin_fee_percentage ? 'percentage' : 'fixed'
      this.editedData.adminFeePercentage = this.parentClaim.admin_fee_percentage || null
      this.editedData.rejectedAmount = this.parentClaim.rejected_amount
      this.editedData.receivedAmount = this.parentClaim.received_amount

      // Set balance assignment for approved/received with rejected amount
      const needsBalance = (this.currentClaimStatus === 'approved' || this.currentClaimStatus === 'received') && this.parentClaim.rejected_amount

      this.editedData.assignBalance = needsBalance ? this.setAssignBalance(this.parentClaim.rejected_amount) : []
    },
    handleStatusTransition() {
      if (this.currentClaimStatus === 'rejected') {
        this.handleTransitionToRejected()
        return
      }

      if (this.currentClaimStatus === 'received') {
        this.handleTransitionToReceived()
        return
      }

      // Default: reset deductions for other statuses
      this.resetDeductions()
      this.editedData.assignBalance = []
    },
    handleTransitionToRejected() {
      this.resetDeductions()
      this.editedData.assignBalance = this.setAssignBalance(this.parentClaim.submitted_amount)
    },
    handleTransitionToReceived() {
      if (this.parentClaim.status === 'submitted') {
        this.handleSubmittedToReceived()
        return
      }
      if (this.parentClaim.status === 'approved') {
        this.handleApprovedToReceived()
        return
      }
      // Default
      this.resetDeductions()
      this.editedData.assignBalance = []
    },
    handleSubmittedToReceived() {
      this.resetDeductions()
      this.editedData.receivedAmount = this.parentClaim.submitted_amount
      this.editedData.assignBalance = []
    },
    handleApprovedToReceived() {
      // Revert to original parentClaim values (readonly behavior)
      this.editedData.adminFee = this.parentClaim.admin_fee || null
      this.editedData.adminFeeType = this.parentClaim.admin_fee_percentage ? 'percentage' : 'fixed'
      this.editedData.adminFeePercentage = this.parentClaim.admin_fee_percentage || null
      this.editedData.rejectedAmount = this.parentClaim.rejected_amount || null

      this.editedData.receivedAmount = this.parentClaim.approved_amount

      // When rejected amount exists, consider balance already assigned from approved stage
      if (this.parentClaim.rejected_amount !== null) {
        this.editedData.assignBalance = this.setAssignBalance(this.parentClaim.rejected_amount)
      } else {
        this.editedData.assignBalance = []
      }
    },
    resetDeductions() {
      this.editedData.adminFee = null
      this.editedData.adminFeeType = 'fixed'
      this.editedData.adminFeePercentage = null
      this.editedData.rejectedAmount = null
      this.editedData.receivedAmount = null
    },
    setAssignBalance(rejectedAmount) {
      if (!rejectedAmount) return []

      const submittedAmount = this.parentClaim.submitted_amount || 0
      const balanceToAssign = submittedAmount - rejectedAmount

      return [
        {
          uid: this.parentClaim.invoices[0],
          amount: balanceToAssign,
          rejected_amount: rejectedAmount
        }
      ]
    },
    getDateByStatus() {
      if (!this.isEdit || !this.parentClaim) {
        return DateTime.local().toFormat('yyyy-MM-dd')
      }

      const statusDateMap = {
        submitted: this.parentClaim.submitted_date,
        approved: this.parentClaim.approved_date,
        rejected: this.parentClaim.rejected_date,
        received: this.parentClaim.received_date
      }

      return statusDateMap[this.currentClaimStatus]
    },
    async fetchInvoiceDetails() {
      this.isFetching = true
      this.isError = false
      try {
        // Get invoiceUid from parentClaim.invoices[0] when in edit mode, otherwise use prop
        const invoiceUid = this.isEdit ? this.parentClaim.invoices[0] : this.invoiceUid
        const { data } = await this.$axios.get(`/panels/${this.company.slug}/invoices/${invoiceUid}`)
        this.invoice = data
      } catch (error) {
        console.error('Error fetching invoice details:', error)
        this.isError = true
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    async onSaveClaim() {
      // Validate form before proceeding
      if (!this.$refs.form.validate()) {
        await this.showMessage(['error', this.$t('panel.fill_required_fields')])
        return
      }
      if (!this.isEdit) {
        await this.handleCreateNewClaim()
        return
      }

      // Edit mode: check if need to assign balance
      if (this.needToAssignBalance && !this.balanceAssigned) {
        this.showAssignBalanceDialog = true
        return
      }

      // Edit mode: check if status changed
      if (this.parentClaim.status !== this.currentClaimStatus) {
        this.showChangeStatusDialog = true
        return
      }

      // Status unchanged, proceed with update
      await this.handleUpdateClaim()
    },
    async handleCreateNewClaim() {
      this.isLoading = true
      try {
        const payload = {
          panel: this.effectivePanelId,
          submitted_date: this.editedData.date,
          remarks: this.editedData.remarks,
          invoices: [this.invoiceUid]
        }

        this.addDocumentsToPayload(payload)

        const { data } = await this.$axios.post(`/panels/${this.company.slug}/claims`, payload)
        await this.showMessage(['success', this.$t('panel.claim_created')])

        await this.cleanupDeletedFiles()

        this.$emit('created', data)

        this.showDialog = false
      } catch (error) {
        console.error('Error creating claim:', error)
        await this.showMessage(['error', this.$t('panel.failed_create_claim')])
      } finally {
        this.isLoading = false
      }
    },
    async handleUpdateReceivedClaim() {
      this.isLoading = true
      try {
        const payload = {
          remarks: this.editedData.remarks
        }

        this.addDocumentsToPayload(payload)

        const { data } = await this.$axios.patch(`/panels/${this.company.slug}/claims/${this.parentClaim.id}`, payload)

        await this.showMessage(['success', this.$t('panel.claim_updated')])

        await this.cleanupDeletedFiles()

        this.$emit('updated', data)

        this.showDialog = false
      } catch (error) {
        console.error('Error updating claim:', error)
        await this.showMessage(['error', this.$t('panel.failed_update_claim')])
      } finally {
        this.isLoading = false
      }
    },
    async handleUpdateClaim(logRemarks) {
      // Special handling for received status - only update remarks and documents
      if (this.parentClaim.status === 'received') {
        await this.handleUpdateReceivedClaim()
        return
      }

      this.isLoading = true
      try {
        // Standard update logic for non-received status
        const payload = {
          status: this.currentClaimStatus,
          remarks: this.editedData.remarks
        }

        this.addDocumentsToPayload(payload)

        if (this.currentClaimStatus === 'submitted') {
          payload.submitted_amount = this.editedData.editClaimAmount.value ?? this.parentClaim.submitted_amount
          payload.submitted_date = this.editedData.date
        }

        if (this.currentClaimStatus === 'approved') {
          payload.approved_date = this.editedData.date
          payload.approved_amount = this.finalAmountAfterDeductions
          payload.admin_fee = this.editedData.adminFee || 0
          payload.admin_fee_percentage = this.editedData.adminFeePercentage || null
          payload.rejected_amount = this.editedData.rejectedAmount || 0
          payload.rejected_date = null
          // Append invoices if balance was assigned
          if (this.balanceAssigned && this.needToAssignBalance && this.editedData.assignBalance.length > 0) {
            payload.invoices = this.editedData.assignBalance
          }
        }
        if (this.currentClaimStatus === 'rejected') {
          payload.approved_date = null
          payload.approved_amount = 0
          payload.admin_fee = 0
          payload.admin_fee_percentage = null
          payload.rejected_date = this.editedData.date
          payload.rejected_amount = this.parentClaim.submitted_amount
          payload.assign_balance = this.setAssignBalance(this.parentClaim.submitted_amount)
        }
        if (this.currentClaimStatus === 'received') {
          payload.received_date = this.editedData.date
          payload.admin_fee = this.editedData.adminFee || 0
          payload.admin_fee_percentage = this.editedData.adminFeePercentage || null
          payload.rejected_amount = this.editedData.rejectedAmount || 0
          payload.received_amount = this.finalAmountAfterDeductions
          // If transitioning from submitted to received, add approved amount and date
          if (this.parentClaim.status === 'submitted') {
            payload.approved_amount = this.parentClaim.submitted_amount - (this.editedData.rejectedAmount || 0)
            payload.approved_date = this.editedData.date
          }
          // Append invoices if balance was assigned
          if (this.balanceAssigned && this.needToAssignBalance && this.editedData.assignBalance.length > 0) {
            payload.invoices = this.editedData.assignBalance
          }
        }

        const { data } = await this.$axios.patch(`/panels/${this.company.slug}/claims/${this.parentClaim.id}`, payload)

        // Log activity for status change
        if (this.parentClaim.status !== this.currentClaimStatus) {
          const isReverting = this.currentClaimStatus === 'submitted'
          await this.$axios.post(`/actions`, {
            company: this.company.id,
            tag: isReverting ? 'status_reverted' : 'status_updated',
            activity: isReverting ? `Panel claim status was reverted from ${this.$options.filters.capitalize(this.parentClaim.status)} to Submitted` : `Panel claim status was updated from ${this.$options.filters.capitalize(this.parentClaim.status)} to ${this.$options.filters.capitalize(this.currentClaimStatus)}`,
            app_model: 'panel.claim',
            object_id: this.parentClaim.id,
            note: logRemarks || null
          })
        }
        // Log activity if amount was edited while status remained submitted
        if (this.currentClaimStatus === 'submitted' && this.editedData.editClaimAmount.value !== null) {
          await this.$axios.post(`/actions`, {
            company: this.company.id,
            tag: 'edited',
            activity: 'Panel claim amount was edited',
            app_model: 'panel.claim',
            object_id: this.parentClaim.id,
            note: this.editedData.editClaimAmount.remarks || null
          })
        }

        await this.showMessage(['success', this.$t('panel.claim_updated')])

        await this.cleanupDeletedFiles()

        this.$emit('updated', data)

        this.showDialog = false
      } catch (error) {
        console.error('Error updating claim:', error)
        await this.showMessage(['error', this.$t('panel.failed_update_claim')])
      } finally {
        this.isLoading = false
      }
    },
    onCloseDialog() {
      // Clean up synced files and files marked for deletion
      const filesToDelete = []

      // Add all synced files
      if (this.allSyncedFiles.length > 0) {
        const syncedIds = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...syncedIds)
      }

      // Add files marked for deletion
      if (this.uploadedFileToDelete.length > 0) {
        const deleteIds = this.uploadedFileToDelete.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...deleteIds)
      }

      // Delete all files if any exist
      if (filesToDelete.length > 0) {
        // Fire and forget - delete files asynchronously without blocking dialog close
        this.$axios.post('/documents/bulk_delete', { documents: filesToDelete }).catch(error => {
          console.error('Error deleting files:', error)
        })
      }

      this.$emit('input', false)
      this.$emit('close')
    },
    onCopyText(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.showMessage(['success', this.$t('panel.copy_successful')])
        })
      }
    },
    onEditAmount() {
      this.showEditAmountDialog = true
    },
    onEditAmountConfirmed(newAmount, remarks) {
      this.editedData.editClaimAmount = { value: newAmount, remarks: remarks }
    },
    onAssignBalance() {
      this.showAssignBalanceDialog = true
    },
    onAssignBalanceConfirmed() {
      this.editedData.assignBalance = this.setAssignBalance(this.editedData.rejectedAmount)
      this.showAssignBalanceDialog = false
    },
    onAssignBalanceCanceled() {
      // Revert rejected amount to original value from parentClaim
      this.editedData.rejectedAmount = this.parentClaim.rejected_amount || null
      // Clear any balance assignments
      this.editedData.assignBalance = []
    },
    onRejectedAmountUpdated() {
      this.editedData.assignBalance = []
    },
    onQrCreated(keyData) {
      this.databinKey = keyData.key
    },
    onQrError(error) {
      console.error('QR Code Error:', error)
      this.showMessage(['error', this.$t('panel.qr_code_error')])
    },
    onDatabinError(error) {
      console.error('Databin Sync Error:', error)
      this.showMessage(['error', this.$t('panel.syncing_files_error')])
    },
    onUploadBatchCompleted(uploadResults) {
      // When files are uploaded, add them to the databin sync
      if (this.$refs.databinSync && uploadResults.length > 0) {
        this.$refs.databinSync.addItems(uploadResults)
      }
    },
    onDeletedUploadedFiles(deletedFiles) {
      // When files are deleted from preview, remove them from databin sync
      if (this.$refs.databinSync && deletedFiles.length > 0) {
        this.$refs.databinSync.removeItems(deletedFiles)
      }
    },
    onOptionsUpdate(options) {
      // Check if sorting has changed
      const sortByChanged = JSON.stringify(options.sortBy) !== JSON.stringify(this.sortBy)
      const sortDescChanged = JSON.stringify(options.sortDesc) !== JSON.stringify(this.sortDesc)

      if (sortByChanged || sortDescChanged) {
        // Reset to first page when sorting changes
        this.currentPage = 1
        // Update tracked sort state
        this.sortBy = options.sortBy
        this.sortDesc = options.sortDesc
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
